import { ComponentRef, KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ErrorSummary,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Select,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { ChatIcon, ChevronRightIcon, PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import type { RoundItem, RoundResult, RoundView } from '../../../api/safetyRounds';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import {
  useAddRoundItemMutation,
  useRemoveRoundItemMutation,
  useRoundQuery,
} from '../../../hooks/queries/safetyRounds/useRoundQueries';
import { longDate, participantsText, shortDate } from '../Plans/plansView';
import {
  categoryOfItem,
  categoryProgress,
  type Choice,
  inManagerArea,
  choiceOf,
  CHOICES,
  descriptionFieldId,
  type DraftAnswer,
  EMPTY_ANSWER,
  initialAnswers,
  itemFieldId,
  OTHER_RESULTS,
  progressOf,
  reasonFieldId,
  type RoundProblem,
  roundsHomeFor,
  roundProblems,
  savedTime,
  segmentsOf,
  startingPoint,
  toAnswerInput,
  unansweredIn,
} from './roundView';
import { useAnswerAutosave } from './useAnswerAutosave';
import { managerProtocolPath, protocolPath } from '../Protocol/protocolView';
import { LoadProblem } from '../LoadProblem';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Round.module.css';

export const RoundPage = (): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams().roundId);
  const { data, isLoading, error } = useRoundQuery(id);

  if (isLoading)
    return (
      <div aria-busy="true" className={layout.loading}>
        <span className="ds-sr-only">{t('safetyRounds.round.loading')}</span>
        <Skeleton variant="rectangle" width="100%" height={480} />
      </div>
    );
  if (error || !data) return <LoadProblem error={error} subject="round" />;
  return <Round key={data.id} round={data} />;
};

type Change = (itemId: number, next: Partial<DraftAnswer>, when: 'now' | 'soon') => void;

/** Typing in a field never answers a checkpoint */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="menu"]'));

/**
 * The height of the sticky bar with the tabs as --round-bar-height on the page, so what is tabbed or scrolled to in the
 * category stops below the bar however many lines the bar has
 */
const useBarHeight = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const page = pageRef.current;
    const bar = barRef.current;
    if (!page || !bar) return;
    const update = () => page.style.setProperty('--round-bar-height', `${bar.offsetHeight}px`);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);
  return { pageRef, barRef };
};

const Round = ({ round }: { round: RoundView }): ReactElement => {
  const { t, i18n } = useTranslation();
  const K = 'safetyRounds.round';
  const [answers, setAnswers] = useState<Record<number, DraftAnswer>>(() => initialAnswers(round));
  // The answers as last changed, so several changes at once («Resten er OK») all count
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [start] = useState(() => startingPoint(round, initialAnswers(round)));
  const [categoryIndex, setCategoryIndex] = useState(start.categoryIndex);
  const [activeId, setActiveId] = useState<number | null>(start.itemId);
  const location = useLocation();
  // Coming from the protocol to correct what is missing, the round says what right away
  const [problems, setProblems] = useState<RoundProblem[] | null>(() =>
    (location.state as { showProblems?: boolean } | null)?.showProblems ? roundProblems(round, initialAnswers(round), t) : null,
  );
  const [waiting, setWaiting] = useState(false);
  // A field to move to once its category is shown: a problem in the summary, or the heading of a new category
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const navigate = useNavigate();
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { pageRef, barRef } = useBarHeight();
  const autosave = useAnswerAutosave(round.id);
  const roles = useGetUserinfo().data?.userRoles ?? [];
  const { pathname } = location;
  const home = roundsHomeFor(roles, pathname);
  // The general manager comes back to the protocol in their own part of the app
  const protocolOfRound = inManagerArea(roles, pathname) ? managerProtocolPath(round.id) : protocolPath(round.id);
  const progress = progressOf(round, answers);
  const editable = round.editable;
  const categories = round.categories;
  const category = categories[Math.min(categoryIndex, categories.length - 1)];
  const isLast = categoryIndex >= categories.length - 1;

  // Checkpoints added or removed come with a new round; keep what was typed for the others
  useEffect(() => {
    setAnswers((current) => ({ ...initialAnswers(round), ...current }));
  }, [round]);

  useEffect(() => {
    if (problems?.length) summaryRef.current?.focus();
  }, [problems]);

  useEffect(() => {
    if (!focusTarget) return;
    const element = document.getElementById(focusTarget);
    // The result of a checkpoint is a group; the choice that is reached with Tab takes the focus
    const target = element?.matches('[role="radiogroup"]') ? element.querySelector<HTMLElement>('[tabindex="0"]') : element;
    target?.focus();
    setFocusTarget(null);
  }, [focusTarget, categoryIndex]);

  // On a phone the tab of the category shown is scrolled into view
  useEffect(() => {
    const tab = document.getElementById(`round-tab-${categories[categoryIndex]?.key}`);
    tab?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [categoryIndex, categories]);

  const change: Change = (itemId, next, when) => {
    const answer = { ...(answersRef.current[itemId] ?? EMPTY_ANSWER), ...next };
    const updated = { ...answersRef.current, [itemId]: answer };
    answersRef.current = updated;
    setAnswers(updated);
    if (problems) setProblems(roundProblems(round, updated, t));
    (when === 'now' ? autosave.saveNow : autosave.saveSoon)(itemId, toAnswerInput(answer));
  };

  const show = (index: number, focus: string | null = null) => {
    const next = categories[index];
    if (!next) return;
    setCategoryIndex(index);
    setActiveId(unansweredIn(next.items, answers)[0] ?? next.items[0]?.id ?? null);
    setFocusTarget(focus ?? 'round-category-heading');
  };

  /** The protocol is built from what is saved, so it opens when the last answers are */
  const openProtocol = () => {
    if (autosave.state === 'saved') navigate(protocolOfRound);
    else setWaiting(true);
  };

  /** The last step: every checkpoint answered as the rules say, then the protocol to sign */
  const finish = () => {
    const found = roundProblems(round, answers, t);
    setProblems(found);
    if (found.length === 0) openProtocol();
  };

  useEffect(() => {
    if (waiting && autosave.state === 'saved') navigate(protocolOfRound);
  }, [waiting, autosave.state, navigate, protocolOfRound]);

  const toProblem = (problem: RoundProblem) => {
    const index = categoryOfItem(round, problem.itemId);
    setCategoryIndex(index);
    setActiveId(problem.itemId);
    setFocusTarget(problem.fieldId);
  };

  // O and A answer the marked checkpoint, ↑ and ↓ move the mark. Never while typing.
  useEffect(() => {
    if (!editable || !category) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || isTyping(event.target)) return;
      const target = event.target as HTMLElement | null;
      const inRound = !target || target === document.body || Boolean(panelRef.current?.contains(target));
      if (!inRound) return;
      const ids = category.items.map((item) => item.id);
      const index = activeId === null ? -1 : ids.indexOf(activeId);
      const key = event.key.toLowerCase();
      if ((key === 'o' || key === 'a') && index >= 0) {
        event.preventDefault();
        change(ids[index], { result: key === 'o' ? 'OK' : 'DEVIATION' }, 'now');
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const next = Math.min(Math.max(index + (event.key === 'ArrowDown' ? 1 : -1), 0), ids.length - 1);
        event.preventDefault();
        setActiveId(ids[next]);
        setFocusTarget(itemFieldId(ids[next]));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const lastSavedAt = autosave.lastSavedAt ?? round.lastSavedAt;
  const saveText =
    autosave.state === 'retrying'
      ? t(`${K}.notSaved`)
      : autosave.state === 'saving'
        ? t(`${K}.saving`)
        : lastSavedAt
          ? t(`${K}.savedAt`, { time: savedTime(lastSavedAt, i18n.language) })
          : t(`${K}.saved`);

  const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = {
      ArrowRight: categoryIndex + 1,
      ArrowLeft: categoryIndex - 1,
      Home: 0,
      End: categories.length - 1,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const index = Math.min(Math.max(moves[event.key], 0), categories.length - 1);
    show(index, `round-tab-${categories[index].key}`);
  };

  if (!category)
    return (
      <div className={layout.loading}>
        <Alert data-color="info">
          <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      </div>
    );

  return (
    <div ref={pageRef} className={`${layout.page} ${classes.page}`} data-size="md">
      <header ref={barRef} className={classes.bar}>
        <div className={classes.barInner}>
          <div className={classes.barText}>
            <nav aria-label={t(`${K}.breadcrumbs`)} className={classes.breadcrumbs}>
              <Link asChild data-size="sm">
                <RouterLink to={home}>{t(`${K}.plans`)}</RouterLink>
              </Link>
              <ChevronRightIcon aria-hidden="true" />
              <Heading level={1} data-size="2xs" className={classes.title} aria-current="page">
                {round.plan.name}
              </Heading>
            </nav>
            <Paragraph data-size="xs" className={classes.meta}>
              {[
                shortDate(round.plannedDate, i18n.language),
                round.participants.length > 0 && t(`${K}.with`, { names: participantsText(round.participants, t).toLowerCase() }),
                t(`${K}.deviations`, { count: progress.deviations }),
              ]
                .filter(Boolean)
                .join(' · ')}
              {' · '}
              <span role="status" data-state={autosave.state} className={classes.saveState}>
                {saveText}
              </span>
            </Paragraph>
          </div>
          <div className={classes.barActions}>
            <span className={classes.counter} aria-label={t(`${K}.answeredLabel`, { done: progress.done, total: progress.total })}>
              <span aria-hidden="true">{t(`${K}.answered`, { done: progress.done, total: progress.total })}</span>
            </span>
            <Button data-size="sm" variant="secondary" onClick={openProtocol}>
              {t(`${K}.seeProtocol`)}
            </Button>
          </div>
        </div>
        <div className={classes.tabsScroll}>
          <div role="tablist" aria-label={t(`${K}.categories`)} className={classes.tabs} onKeyDown={onTabKey}>
            {categories.map((entry, index) => {
              const stats = categoryProgress(entry.items, answers);
              const selected = index === categoryIndex;
              return (
                <button
                  key={entry.key}
                  type="button"
                  role="tab"
                  id={`round-tab-${entry.key}`}
                  aria-selected={selected}
                  aria-controls="round-panel"
                  aria-label={[
                    t(`${K}.tabLabel`, { name: entry.name, done: stats.done, total: stats.total }),
                    stats.deviations > 0 && t(`${K}.tabDeviations`, { count: stats.deviations }),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  tabIndex={selected ? 0 : -1}
                  className={classes.tab}
                  onClick={() => show(index, `round-tab-${entry.key}`)}
                >
                  <span className={classes.tabText} aria-hidden="true">
                    <span className={classes.tabName}>{entry.name}</span>
                    {stats.deviations > 0 ? (
                      <span className={classes.tabDeviations}>{t(`${K}.tabDeviations`, { count: stats.deviations })}</span>
                    ) : (
                      <span className={classes.tabCount}>{t(`${K}.answered`, stats)}</span>
                    )}
                  </span>
                  <span className={classes.segments} aria-hidden="true">
                    {segmentsOf(entry.items, answers).map((segment, position) => (
                      <span key={position} data-segment={segment} />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className={classes.content}>
        {/* The general manager may walk the round, but it is the safety representative's to walk and sign first */}
        {editable && round.readerIsSafetyRep === false && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">
              {round.safetyRepEmail
                ? t(`${K}.managerWalks`, { email: round.safetyRepEmail })
                : t(`${K}.managerWalksNoSafetyRep`)}
            </Paragraph>
          </Alert>
        )}
        {editable && round.sentBack && (
          <Alert data-color="warning" data-size="sm">
            <Heading level={2} data-size="2xs">
              {t(`${K}.sentBack`, {
                who: round.sentBack.byEmail ?? t(`${K}.generalManager`),
                date: longDate(round.sentBack.createdAt.slice(0, 10), i18n.language),
              })}
            </Heading>
            <Paragraph data-size="sm">{round.sentBack.comment}</Paragraph>
          </Alert>
        )}
        {!editable && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">{round.mayConduct ? t(`${K}.locked`) : t(`${K}.readOnly`)}</Paragraph>
          </Alert>
        )}
        {problems && problems.length > 0 && (
          <ErrorSummary ref={summaryRef}>
            <ErrorSummary.Heading>{t(`${K}.errorSummary`, { count: problems.length })}</ErrorSummary.Heading>
            <ErrorSummary.List>
              {problems.slice(0, 10).map((problem) => (
                <ErrorSummary.Item key={problem.fieldId}>
                  <ErrorSummary.Link
                    href={`#${problem.fieldId}`}
                    onClick={(event) => {
                      // The checkpoint may be in another category, which is shown first
                      event.preventDefault();
                      toProblem(problem);
                    }}
                  >
                    {problem.message}
                  </ErrorSummary.Link>
                </ErrorSummary.Item>
              ))}
            </ErrorSummary.List>
          </ErrorSummary>
        )}
        {waiting && (
          <Alert data-color={autosave.state === 'retrying' ? 'warning' : 'info'} data-size="sm" role="status">
            <Paragraph data-size="sm">{autosave.state === 'retrying' ? t(`${K}.waitingOffline`) : t(`${K}.waiting`)}</Paragraph>
          </Alert>
        )}

        <div
          ref={panelRef}
          id="round-panel"
          role="tabpanel"
          aria-labelledby={`round-tab-${category.key}`}
          className={classes.card}
        >
          <Category
            key={category.key}
            roundId={round.id}
            category={category}
            answers={answers}
            people={round.people}
            editable={editable}
            problems={problems ?? []}
            activeId={activeId}
            onActive={setActiveId}
            onChange={change}
          />
          <div className={classes.footer}>
            {categoryIndex > 0 ? (
              <Button
                data-size="sm"
                variant="tertiary"
                className={classes.previous}
                onClick={() => show(categoryIndex - 1)}
              >
                {t(`${K}.previous`)} <span>{categories[categoryIndex - 1].name}</span>
              </Button>
            ) : (
              <span />
            )}
            {!isLast ? (
              <Button data-size="sm" variant="secondary" onClick={() => show(categoryIndex + 1)}>
                {t(`${K}.next`)} {categories[categoryIndex + 1].name}
              </Button>
            ) : editable ? (
              <Button data-size="sm" onClick={finish}>
                {t(`${K}.finishAction`)}
              </Button>
            ) : (
              <Button data-size="sm" variant="secondary" onClick={openProtocol}>
                {t(`${K}.seeProtocol`)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type CategoryProps = {
  roundId: number;
  category: RoundView['categories'][number];
  answers: Record<number, DraftAnswer>;
  people: RoundView['people'];
  editable: boolean;
  problems: RoundProblem[];
  activeId: number | null;
  onActive: (itemId: number) => void;
  onChange: Change;
};

/** One category at a time, with «Resten er OK» for what is left */
const Category = ({
  roundId,
  category,
  answers,
  people,
  editable,
  problems,
  activeId,
  onActive,
  onChange,
}: CategoryProps): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.round';
  const add = useAddRoundItemMutation(roundId);
  const remove = useRemoveRoundItemMutation(roundId);
  const [adding, setAdding] = useState<string | null>(null);
  const stats = categoryProgress(category.items, answers);
  const open = unansweredIn(category.items, answers);
  const inputId = `round-add-${category.key}`;

  return (
    <>
      <div className={classes.categoryHead}>
        <div className={classes.categoryTitle}>
          <Heading level={2} data-size="xs" id="round-category-heading" tabIndex={-1}>
            {category.number}. {category.name}
          </Heading>
          <span className={layout.subtle}>{t(`${K}.categoryProgress`, stats)}</span>
        </div>
        {editable && open.length > 0 && (
          <Button
            data-size="sm"
            variant="secondary"
            onClick={() => open.forEach((itemId) => onChange(itemId, { result: 'OK' }, 'now'))}
          >
            {open.length === category.items.length ? t(`${K}.allOk`) : t(`${K}.restOk`)}
          </Button>
        )}
      </div>
      <ol className={classes.items}>
        {category.items.map((item) => (
          <Item
            key={item.id}
            item={item}
            answer={answers[item.id] ?? EMPTY_ANSWER}
            people={people}
            editable={editable}
            active={editable && item.id === activeId}
            problem={problems.find((problem) => problem.itemId === item.id)}
            onActive={() => onActive(item.id)}
            onChange={(next, when) => onChange(item.id, next, when)}
            onRemove={item.source === 'ROUND' && editable ? () => remove.mutate(item.id) : undefined}
          />
        ))}
      </ol>
      {editable && (
        <div className={classes.add}>
          {adding === null ? (
            <Button data-size="sm" variant="tertiary" onClick={() => setAdding('')}>
              <PlusIcon aria-hidden="true" />
              {t(`${K}.addCheckpoint`)}
            </Button>
          ) : (
            <div className={classes.addRow}>
              <Field>
                <Label htmlFor={inputId} data-size="sm">
                  {t(`${K}.newCheckpoint`)}
                </Label>
                <Input id={inputId} data-size="sm" value={adding} onChange={(event) => setAdding(event.target.value)} />
              </Field>
              <Button
                data-size="sm"
                variant="secondary"
                aria-busy={add.isPending || undefined}
                onClick={() => {
                  if (!adding.trim()) return setAdding(null);
                  add.mutate({ categoryKey: category.key, text: adding.trim() }, { onSuccess: () => setAdding(null) });
                }}
              >
                {t(`${K}.add`)}
              </Button>
            </div>
          )}
          {(add.isError || remove.isError) && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
            </Alert>
          )}
        </div>
      )}
    </>
  );
};

type ItemProps = {
  item: RoundItem;
  answer: DraftAnswer;
  people: RoundView['people'];
  editable: boolean;
  active: boolean;
  problem: RoundProblem | undefined;
  onActive: () => void;
  onChange: (next: Partial<DraftAnswer>, when: 'now' | 'soon') => void;
  onRemove?: () => void;
};

const Item = ({ item, answer, people, editable, active, problem, onActive, onChange, onRemove }: ItemProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const K = 'safetyRounds.round';
  const [commenting, setCommenting] = useState(false);
  const notChecked = answer.result === 'NOT_CHECKED';
  const deviation = answer.result === 'DEVIATION';
  const textId = `round-text-${item.id}`;
  const commentId = `round-comment-${item.id}`;

  return (
    <li
      className={classes.item}
      data-active={active || undefined}
      data-result={answer.result ?? 'none'}
      onFocusCapture={onActive}
      onClick={onActive}
    >
      <div className={classes.row}>
        <span className={classes.number}>{item.number}</span>
        <div className={classes.itemText}>
          <span id={textId}>{item.text}</span>
          {item.help && <span className={classes.help}>{item.help}</span>}
          {item.source === 'ROUND' && (
            <Tag data-color="info" data-size="sm">
              {t(`${K}.addedHere`)}
            </Tag>
          )}
        </div>
        <div className={classes.controls}>
          {editable ? (
            <Choices item={item} result={answer.result} invalid={problem?.fieldId === itemFieldId(item.id)} onChange={onChange} />
          ) : (
            <span className={classes.result} data-result={answer.result ?? 'none'}>
              {answer.result ? t(`${K}.results.${answer.result}`) : t('safetyRounds.protocol.unanswered')}
            </span>
          )}
          {editable && (
            <Button
              data-size="sm"
              variant="tertiary"
              icon
              aria-label={t(`${K}.commentFor`, { number: item.number })}
              aria-expanded={notChecked ? undefined : commenting}
              aria-controls={notChecked ? reasonFieldId(item.id) : commenting ? commentId : undefined}
              className={classes.commentButton}
              data-pressed={commenting || Boolean(answer.comment) || undefined}
              onClick={() => {
                // The reason of a checkpoint not checked is its comment
                if (notChecked) return document.getElementById(reasonFieldId(item.id))?.focus();
                if (commenting) onChange({}, 'now');
                setCommenting(!commenting);
              }}
            >
              <ChatIcon aria-hidden="true" />
            </Button>
          )}
          {onRemove && (
            <Button data-size="sm" variant="tertiary" icon aria-label={t(`${K}.remove`, { number: item.number })} onClick={onRemove}>
              <TrashIcon aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
      {problem?.fieldId === itemFieldId(item.id) && (
        <ValidationMessage data-size="sm" className={classes.detail}>
          {problem.message}
        </ValidationMessage>
      )}

      {deviation && editable && (
        <div className={`${classes.detail} ${classes.deviation}`}>
          <Field>
            <Label htmlFor={descriptionFieldId(item.id)} data-size="sm">
              {t(`${K}.description`)} <span className={classes.labelHint}>{t(`${K}.descriptionHelp`)}</span>
            </Label>
            <Textarea
              id={descriptionFieldId(item.id)}
              data-size="sm"
              rows={2}
              value={answer.description}
              aria-invalid={problem?.fieldId === descriptionFieldId(item.id) ? true : undefined}
              onChange={(event) => onChange({ description: event.target.value }, 'soon')}
              onBlur={() => onChange({}, 'now')}
            />
            {problem?.fieldId === descriptionFieldId(item.id) && <ValidationMessage>{problem.message}</ValidationMessage>}
          </Field>
          <div className={classes.twoFields}>
            <Field>
              <Label htmlFor={`round-responsible-${item.id}`} data-size="sm">
                {t(`${K}.responsible`)}
              </Label>
              <Select
                id={`round-responsible-${item.id}`}
                data-size="sm"
                value={answer.responsibleId ?? ''}
                onChange={(event) => onChange({ responsibleId: event.target.value ? Number(event.target.value) : null }, 'now')}
              >
                <Select.Option value="">{t(`${K}.responsibleDefault`)}</Select.Option>
                {people.map((person) => (
                  <Select.Option key={person.userId} value={person.userId}>
                    {person.email}
                  </Select.Option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor={`round-due-${item.id}`} data-size="sm">
                {t(`${K}.due`)}
              </Label>
              <Input
                id={`round-due-${item.id}`}
                data-size="sm"
                type="date"
                value={answer.dueDate}
                onChange={(event) => onChange({ dueDate: event.target.value }, 'now')}
              />
            </Field>
          </div>
          {!answer.dueDate && (
            <Paragraph data-size="xs" className={layout.subtle}>
              {t(`${K}.dueHelp`)}
            </Paragraph>
          )}
        </div>
      )}
      {deviation && !editable && (
        <div className={`${classes.detail} ${classes.deviation}`}>
          <Paragraph data-size="sm">{answer.description}</Paragraph>
          <Paragraph data-size="xs" className={layout.subtle}>
            {[
              `${t(`${K}.responsible`)}: ${people.find((person) => person.userId === answer.responsibleId)?.email ?? t('safetyRounds.protocol.defaultResponsible')}`,
              `${t(`${K}.due`)}: ${answer.dueDate ? longDate(answer.dueDate, i18n.language) : t('safetyRounds.protocol.defaultDue')}`,
            ].join(' · ')}
          </Paragraph>
        </div>
      )}

      {notChecked && (
        <Field className={classes.detail}>
          <Label htmlFor={reasonFieldId(item.id)} data-size="sm">
            {t(`${K}.reason`)}
          </Label>
          <Textarea
            id={reasonFieldId(item.id)}
            data-size="sm"
            rows={2}
            readOnly={!editable}
            value={answer.comment}
            aria-invalid={problem?.fieldId === reasonFieldId(item.id) ? true : undefined}
            onChange={(event) => onChange({ comment: event.target.value }, 'soon')}
            onBlur={() => onChange({}, 'now')}
          />
          {problem?.fieldId === reasonFieldId(item.id) && <ValidationMessage>{problem.message}</ValidationMessage>}
        </Field>
      )}

      {!notChecked && editable && commenting && (
        <div className={`${classes.detail} ${classes.comment}`}>
          <Label htmlFor={commentId} data-size="sm">
            {t(`${K}.commentLabel`, { number: item.number })}
          </Label>
          <div className={classes.commentRow}>
            <Input
              id={commentId}
              data-size="sm"
              autoFocus
              placeholder={t(`${K}.commentPlaceholder`)}
              value={answer.comment}
              onChange={(event) => onChange({ comment: event.target.value }, 'soon')}
              onBlur={() => onChange({}, 'now')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault();
                  onChange({}, 'now');
                  setCommenting(false);
                }
              }}
            />
            <Button
              data-size="sm"
              variant="secondary"
              onClick={() => {
                onChange({}, 'now');
                setCommenting(false);
              }}
            >
              {t(`${K}.done`)}
            </Button>
          </div>
        </div>
      )}
      {!notChecked && answer.comment && !(editable && commenting) && (
        <Paragraph data-size="xs" className={`${classes.detail} ${layout.subtle}`}>
          {t(`${K}.comment`)}: {answer.comment}
        </Paragraph>
      )}
    </li>
  );
};

/**
 * OK, Avvik or Annet as one segmented control. Annet opens a small menu: not checked (a reason is
 * required) or not relevant. ← → move between the choices, Space or Enter picks one.
 */
const Choices = ({
  item,
  result,
  invalid,
  onChange,
}: {
  item: RoundItem;
  result: RoundResult | null;
  invalid: boolean;
  onChange: (next: Partial<DraftAnswer>, when: 'now' | 'soon') => void;
}): ReactElement => {
  const { t } = useTranslation();
  const K = 'safetyRounds.round';
  const [menuOpen, setMenuOpen] = useState(false);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const chosen = choiceOf(result);
  const focusable = chosen ? CHOICES.indexOf(chosen) : 0;
  const menuId = `round-other-${item.id}`;

  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitemradio"]')?.focus();
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && event.target !== buttons.current[2]) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const pick = (choice: Choice) => {
    if (choice === 'OTHER') return setMenuOpen(!menuOpen);
    setMenuOpen(false);
    onChange({ result: choice }, 'now');
  };

  const pickOther = (other: (typeof OTHER_RESULTS)[number]) => {
    setMenuOpen(false);
    onChange({ result: other }, 'now');
    buttons.current[2]?.focus();
  };

  const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    buttons.current[(index + step + CHOICES.length) % CHOICES.length]?.focus();
  };

  return (
    <div className={classes.choicesWrap}>
      <div
        role="radiogroup"
        id={itemFieldId(item.id)}
        aria-label={t(`${K}.resultFor`, { number: item.number, text: item.text })}
        aria-invalid={invalid || undefined}
        className={classes.choices}
      >
        {CHOICES.map((choice, index) => {
          const checked = chosen === choice;
          const label = choice === 'OTHER' && checked && result ? t(`${K}.results.${result}`) : t(`${K}.choices.${choice}`);
          return (
            <button
              key={choice}
              ref={(element) => {
                buttons.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={index === focusable ? 0 : -1}
              data-choice={choice}
              data-result={checked ? result : undefined}
              className={classes.choice}
              {...(choice === 'OTHER'
                ? { 'aria-haspopup': 'menu' as const, 'aria-expanded': menuOpen, 'aria-controls': menuOpen ? menuId : undefined }
                : {})}
              onClick={() => pick(choice)}
              onKeyDown={(event) => onKey(event, index)}
            >
              {label}
            </button>
          );
        })}
      </div>
      {menuOpen && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t(`${K}.otherMenu`, { number: item.number })}
          className={classes.menu}
          onKeyDown={(event) => {
            const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [])];
            const index = items.indexOf(document.activeElement as HTMLElement);
            if (event.key === 'Escape' || event.key === 'Tab') {
              if (event.key === 'Escape') event.preventDefault();
              setMenuOpen(false);
              buttons.current[2]?.focus();
            } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
            }
          }}
        >
          {OTHER_RESULTS.map((other) => (
            <button
              key={other}
              type="button"
              role="menuitemradio"
              aria-checked={result === other}
              className={classes.menuItem}
              onClick={() => pickOther(other)}
            >
              <span>{t(`${K}.results.${other}`)}</span>
              <span className={classes.menuHint}>{t(`${K}.other.${other}`)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
